import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { Shield, ShieldCheck, ShieldOff, Smartphone, Copy, Check, Loader2, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const TwoFactorSetup = () => {
  const { user, getToken, refreshUser } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  const [copied, setCopied] = useState(false);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/api/auth/2fa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIs2FAEnabled(response.data.enabled);
    } catch (error) {
      console.error('Failed to check 2FA status:', error);
    }
  };

  const initiateSetup = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.post(`${API}/api/auth/2fa/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSetupData(response.data);
    } catch (error) {
      toast({
        title: "Setup Failed",
        description: error.response?.data?.detail || "Failed to initiate 2FA setup",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const enable2FA = async (e) => {
    e.preventDefault();
    if (verificationCode.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter a 6-digit code from your authenticator app",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      await axios.post(`${API}/api/auth/2fa/enable`, { code: verificationCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "2FA Enabled",
        description: "Two-factor authentication has been enabled on your account"
      });
      setSetupData(null);
      setVerificationCode('');
      setIs2FAEnabled(true);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast({
        title: "Verification Failed",
        description: error.response?.data?.detail || "Invalid verification code",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async (e) => {
    e.preventDefault();
    if (disableCode.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter a 6-digit code from your authenticator app",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      await axios.post(`${API}/api/auth/2fa/disable`, { code: disableCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "2FA Disabled",
        description: "Two-factor authentication has been disabled"
      });
      setShowDisable(false);
      setDisableCode('');
      setIs2FAEnabled(false);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast({
        title: "Disable Failed",
        description: error.response?.data?.detail || "Invalid verification code",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text.replace(/\s/g, ''));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: "Copied", description: "Secret key copied to clipboard" });
  };

  // 2FA Already Enabled
  if (is2FAEnabled) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-700">
            <ShieldCheck className="w-5 h-5" />
            Two-Factor Authentication Enabled
          </CardTitle>
          <CardDescription className="text-green-600">
            Your account is protected with two-factor authentication
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!showDisable ? (
            <Button 
              variant="outline" 
              onClick={() => setShowDisable(true)}
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="2fa-disable-btn"
            >
              <ShieldOff className="w-4 h-4 mr-2" />
              Disable 2FA
            </Button>
          ) : (
            <form onSubmit={disable2FA} className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-700">Warning</p>
                    <p className="text-sm text-red-600">
                      Disabling 2FA will make your account less secure. Enter your current authenticator code to confirm.
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Authenticator Code
                </label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                  className="font-mono text-center text-xl tracking-widest"
                  data-testid="2fa-disable-code-input"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  type="submit" 
                  variant="destructive"
                  disabled={loading || disableCode.length !== 6}
                  data-testid="2fa-disable-confirm-btn"
                >
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Disable 2FA
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => { setShowDisable(false); setDisableCode(''); }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    );
  }

  // Setup not started
  if (!setupData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account by requiring a verification code from your mobile device when signing in.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={initiateSetup} disabled={loading} data-testid="2fa-setup-btn">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Setting up...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4 mr-2" />
                Enable Two-Factor Authentication
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Setup in progress - show QR code
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="w-5 h-5" />
          Set Up Your Authenticator
        </CardTitle>
        <CardDescription>
          Scan the QR code with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* QR Code */}
        <div className="flex justify-center">
          <div className="p-4 bg-white rounded-lg shadow-inner border">
            <img 
              src={setupData.qr_code} 
              alt="2FA QR Code" 
              className="w-48 h-48"
              data-testid="2fa-qr-code"
            />
          </div>
        </div>

        {/* Manual Entry */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">
            Can't scan the code? Enter this key manually:
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 p-2 bg-white border rounded font-mono text-sm tracking-wider">
              {setupData.manual_entry_key}
            </code>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => copyToClipboard(setupData.manual_entry_key)}
              data-testid="2fa-copy-secret-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Verification */}
        <form onSubmit={enable2FA} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Enter the 6-digit code from your authenticator app
            </label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
              className="font-mono text-center text-2xl tracking-widest"
              autoComplete="one-time-code"
              data-testid="2fa-verify-code-input"
            />
          </div>
          <div className="flex gap-2">
            <Button 
              type="submit" 
              disabled={loading || verificationCode.length !== 6}
              data-testid="2fa-verify-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify and Enable'
              )}
            </Button>
            <Button 
              type="button" 
              variant="outline"
              onClick={() => { setSetupData(null); setVerificationCode(''); }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default TwoFactorSetup;
